import {monthCalendar, fromObject} from 'common/date';
import {ChevronLeft, ChevronRight} from 'lucide-react';
import * as React from 'react';
import {Button} from 'src/Button';
import {cn} from 'src/lib/utils';
import {Select} from 'src/Select';
import type {DateTime} from 'luxon';

// prettier-ignore
const months = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const cellStyle =
  'flex size-8 flex-col items-center justify-center transition-none';

type Props = React.ComponentProps<'div'> & {
  selectedDate: DateTime;
  onDateSelect: (date: DateTime) => void;
};

const CalendarSelect = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {selectedDate, onDateSelect, className, ...divProps} = props;

  const [year, setYear] = React.useState<number>(selectedDate.year);
  const [month, setMonth] = React.useState<number>(selectedDate.month);

  React.useEffect(() => {
    // Check if date is visible in the current month
    const firstOfMonth = fromObject({year, month, day: 1});
    const earliest = firstOfMonth.startOf('week').minus({days: 1});
    const latest = firstOfMonth
      .endOf('month')
      .plus({days: 1})
      .endOf('week')
      .minus({days: 1});

    if (selectedDate < earliest || selectedDate > latest) {
      setYear(selectedDate.year);
      setMonth(selectedDate.month);
    }
  }, [selectedDate]);

  const handlePreviousMonth = () => {
    const newDate = fromObject({year, month, day: 1}).minus({months: 1});
    setMonth(newDate.month);
    setYear(newDate.year);
  };

  const handleNextMonth = () => {
    const newDate = fromObject({year, month, day: 1}).plus({months: 1});
    setMonth(newDate.month);
    setYear(newDate.year);
  };

  const renderDay = (date: DateTime, i: number) => {
    const isCurrentMonth = date.month === month;
    const isSelected = date.equals(selectedDate);
    const className = cn(
      cellStyle,
      !isCurrentMonth && 'text-muted-foreground',
      isSelected && 'bg-primary text-primary-foreground hover:bg-primary/80',
    );
    return (
      <Button
        variant="ghost"
        key={i}
        tabIndex={-1}
        onClick={() => onDateSelect(date)}
        className={className}
      >
        {date.day}
      </Button>
    );
  };

  const monthSelect = (
    <Select
      options={months}
      value={months[month - 1]}
      onChange={(value) => setMonth(months.indexOf(value) + 1)}
    />
  );

  const currentYear = new Date().getFullYear();
  const yearSelect = (
    <Select
      options={Array.from({length: 20}, (_, i) => i + currentYear - 15).map(
        (year) => year.toString(),
      )}
      value={year.toString()}
      onChange={(value) => setYear(parseInt(value))}
    />
  );

  return (
    <div
      className={cn(
        'bg-background ring-border mt-1 rounded-md p-2 shadow-md ring-1',
        className,
      )}
      ref={ref}
      {...divProps}
    >
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          className={cellStyle}
          tabIndex={-1}
          onClick={handlePreviousMonth}
        >
          <ChevronLeft className="size-4" />
        </Button>
        {monthSelect}
        {yearSelect}
        <Button
          variant="ghost"
          className={cellStyle}
          tabIndex={-1}
          onClick={handleNextMonth}
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
      <div className="text-muted-foreground flex flex-row">
        <div className={cellStyle}>Su</div>
        <div className={cellStyle}>Mo</div>
        <div className={cellStyle}>Tu</div>
        <div className={cellStyle}>We</div>
        <div className={cellStyle}>Th</div>
        <div className={cellStyle}>Fr</div>
        <div className={cellStyle}>Sa</div>
      </div>
      <div className="flex flex-col">
        {monthCalendar(year, month).map((week: DateTime[], i: number) => (
          <div className="flex flex-row" key={i}>
            {week.map((d: DateTime, j: number) => renderDay(d, j))}
          </div>
        ))}
      </div>
    </div>
  );
});

export default CalendarSelect;
